import {
  STORAGE_KEYS,
  getItem,
  setItem,
  removeItem,
  clearAuthStorage,
  getRawItem,
} from '../../lib/storage';
import {
  decodeToken,
  isTokenValid as checkTokenValid,
  isTokenExpiringSoon,
  clearTokenCache,
  type DecodedToken,
} from '../../lib/storage/tokenCache';

export interface UserData {
  id: string;
  email: string;
  fullName: string;
  firstName: string;
  lastName: string;
  username: string;
  language: string;
  phone: string;
  emailVerified: boolean;
  role: string;
  provider: string;
  passwordSet: boolean;
  joiningDate: string | null;
  lastSignInAt?: string;
  emailConfirmedAt?: string;
  profile: {
    country: string;
    city: string;
    zipCode: string;
    bio: string;
    profileImage: string;
    coverImage: string;
  };
  isEarthIdVerified: boolean;
  earthIdData: Record<string, unknown>;
  user_metadata?: Record<string, unknown>;
}

export function getAccessToken(): string | null {
  return getRawItem(STORAGE_KEYS.ACCESS_TOKEN);
}

export function getRefreshToken(): string | null {
  return getRawItem(STORAGE_KEYS.REFRESH_TOKEN);
}

export function setTokens(accessToken: string, refreshToken?: string): void {
  setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
  if (refreshToken) {
    setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
  }
}

export function clearTokens(): void {
  clearAuthStorage();
  clearTokenCache();
}

export function isTokenValid(): boolean {
  const token = getAccessToken();
  return checkTokenValid(token);
}

export function shouldRefreshToken(bufferSeconds = 60): boolean {
  const token = getAccessToken();
  return isTokenExpiringSoon(token, bufferSeconds);
}

export function getUserDataFromToken(token: string | null): UserData | null {
  if (!token) return null;

  const decoded = decodeToken(token);
  if (!decoded) return null;

  const userMetadata = decoded.user_metadata || {};
  const appMetadata = decoded.app_metadata || {};

  const firstName = userMetadata.first_name || '';
  const lastName = userMetadata.last_name || '';
  const fullName =
    firstName && lastName
      ? `${firstName} ${lastName}`.trim()
      : userMetadata.full_name || decoded.name || '';

  const profile = userMetadata.profile || {};
  const earthidVerification =
    userMetadata.earthid_verification ||
    userMetadata.earthIdVerification ||
    decoded.raw_user_meta_data?.earthid_verification ||
    {};

  return {
    id: decoded.sub,
    email: decoded.email,
    fullName,
    firstName,
    lastName,
    username: userMetadata.user_name || '',
    language: userMetadata.language || 'en',
    phone: decoded.phone || '',
    emailVerified: decoded.email_verified || false,
    role: decoded.role || 'authenticated',
    provider: appMetadata.provider || 'email',
    passwordSet: userMetadata.password_set === true,
    joiningDate: decoded.iat ? new Date(decoded.iat * 1000).toISOString() : null,
    profile: {
      country: profile.country || '',
      city: profile.city || '',
      zipCode: profile.zipCode || '',
      bio: profile.bio || '',
      profileImage: profile.profileImage || '',
      coverImage: profile.coverImage || '',
    },
    isEarthIdVerified: earthidVerification.is_verified || false,
    earthIdData: earthidVerification,
    user_metadata: userMetadata,
  };
}

export function getCachedUserData(): UserData | null {
  return getItem<UserData>(STORAGE_KEYS.USER_DATA);
}

export function saveUserData(userData: UserData): void {
  setItem(STORAGE_KEYS.USER_DATA, userData);
}

export function clearUserData(): void {
  removeItem(STORAGE_KEYS.USER_DATA);
}

export function getDecodedToken(): DecodedToken | null {
  const token = getAccessToken();
  return decodeToken(token);
}
